import React, { useState, useRef, useEffect } from "react";
import { getHeroIcon } from "../Common/Icons/HeroIcons";
import renderWithLineBreaks from "../../Data/mappers/renderWithLineBreaks";

const IMAGE_EXT = /\.(jpg|jpeg|png|webp|gif|svg)$/i;
const MOBILE_BREAKPOINT_QUERY = "(max-width: 767px)";

const ModelHero = ({
  title,
  tagline,
  videoSrc,
  heroInfo,
  allNew = false,
  newBadgeLabel = "Nueva",
  twoPerRow = false,
}) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches,
  );
  const videoRef = useRef(null);
  // Get current source based on device
  const currentSrc = isMobile ? videoSrc?.mobile : videoSrc?.desktop;

  // Compute synchronously so the video element is never mounted for image sources
  const isImage = videoSrc
    ? IMAGE_EXT.test(isMobile ? videoSrc.mobile || "" : videoSrc.desktop || "")
    : false;

  // Reset video state when source changes (car selection or mobile/desktop swap)
  useEffect(() => {
    if (videoRef.current && !isImage) {
      // Stop any current playback
      videoRef.current.pause();

      // Reset loading state
      setIsVideoLoaded(false);
      setIsPlaying(false);
      setHasVideoError(false);

      // Force the video element to reload
      videoRef.current.load();
    }
  }, [currentSrc, isImage]);

  // Auto-play video when loaded (both mobile and desktop)
  useEffect(() => {
    if (videoRef.current && isVideoLoaded && !isImage) {
      const playPromise = videoRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error("Autoplay was prevented:", error);
            setIsPlaying(false);
          });
      }
    }
  }, [isVideoLoaded, isImage]);

  // React to breakpoint changes so source switches without full page reload.
  useEffect(() => {
    const mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const handleBreakpointChange = (event) => setIsMobile(event.matches);

    setIsMobile(mobileMediaQuery.matches);

    if (mobileMediaQuery.addEventListener) {
      mobileMediaQuery.addEventListener("change", handleBreakpointChange);
      return () =>
        mobileMediaQuery.removeEventListener("change", handleBreakpointChange);
    }

    mobileMediaQuery.addListener(handleBreakpointChange);
    return () => mobileMediaQuery.removeListener(handleBreakpointChange);
  }, []);

  const handleVideoLoaded = () => {
    // Enable hardware acceleration to improve video quality
    if (videoRef.current) {
      videoRef.current.style.transform = "translateZ(0)";
      videoRef.current.style.webkitTransform = "translateZ(0)";
      // Force highest quality playback
      videoRef.current.playbackQuality = "high";
    }
    setIsVideoLoaded(true);
    setHasVideoError(false);
  };

  const handleVideoError = (e) => {
    console.error("Video failed to load:", videoSrc, e);
    setIsVideoLoaded(false);
    setHasVideoError(true);
  };

  // Determine if we should show the video element
  const showVideo = videoSrc && !hasVideoError && !isImage;
  // Determine if we should show the image element
  const showImage = videoSrc && isImage;

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Video/Image Background */}
      <div className="absolute inset-0 w-full h-full bg-black">
        {/* Image element - only rendered if source is an image */}
        {showImage && (
          <img
            src={currentSrc}
            alt={title}
            className="w-full h-full object-cover object-bottom"
          />
        )}

        {/* Video element - only rendered if we have a valid video source */}
        {showVideo && (
          <div className="absolute inset-0 w-full">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              muted
              playsInline
              loop
              preload="auto"
              key={currentSrc}
              onLoadedData={handleVideoLoaded}
              onError={handleVideoError}>
              {/* Proporciona múltiples formatos de fuente para mejor compatibilidad */}
              <source src={currentSrc} type="video/mp4" />
              {/* Puedes agregar más formatos de fuente si están disponibles */}
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>

      {/* Text Overlay - No dark background on mobile */}
      <div className=" md:pb-0 h-full md:h-auto absolute md:bg-black md:bg-opacity-50 top-0 md:top-auto bottom-0 left-0 right-0 flex text-white mx-auto z-10 pointer-events-none">
        <div className=" mx-auto w-full md:py-[30px]">
          <div className="pt-[80px] md:pt-0 relative flex flex-col justify-between items-center flex-wrap md:flex-row h-full pointer-events-auto md:px-16">
            {/* Title section - No overlay on mobile */}
            <div className="flex flex-col relative mt-5 md:mt-0 pl-4 flex-shrink-0 w-full md:w-fit">
              {/* Decorative border line */}
              <div
                className={`hidden md:block absolute left-0 w-[1px] bg-gray-500 ${allNew ? "h-[90px]" : "h-10 md:h-[66px]"}`}></div>

              {allNew && (
                <span className="font-light text-2xl md:text-3xl leading-tight">
                  {newBadgeLabel}
                </span>
              )}
              <h1 className="font-bold mb-1">{title}</h1>
              <h3 className=" font-normal">{tagline}</h3>
              <div className="md:hidden h-[1px] md:h-20 w-7 mt-1 bg-white  md:mx-16"></div>
            </div>

            <div className="bg-black bg-opacity-50 md:bg-transparent w-full xl:w-fit justify-center">
              <div
                className={`justify-between mx-auto lg:mx-0 p-4 ${
                  twoPerRow
                    ? "grid grid-cols-2 gap-y-2 md:flex md:flex-wrap"
                    : "flex flex-wrap"
                }`}>
                {heroInfo.map((item, index) => (
                  <div
                    key={item.id || item.iconId || index}
                    className={`${
                      twoPerRow ? "w-full" : "w-fit min-w-[100px]"
                    } md:w-fit flex flex-col items-center text-center py-2 xl:p-2`}>
                    <div className="flex justify-center items-end h-8 w-full md:h-10">
                      {getHeroIcon(item.iconId, "w-6 h-6 md:h-8 md:w-8")}
                    </div>
                    <h6 className="md:hidden mt-1 whitespace-pre-line font-normal text-nowrap">
                      {(item.description_mobile || item.description).replace(
                        /<br\/>/g,
                        "\n",
                      )}
                    </h6>
                    <h6 className="hidden md:block mt-1 font-normal text-center min-w-[80px] text-pretty">
                      {renderWithLineBreaks(item.description)}
                    </h6>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelHero;
